import {
  connect,
  IntentCtx,
  RenderConfigScreenCtx,
  Field,
  FieldIntentCtx,
  RenderFieldExtensionCtx,
  RenderPageCtx,
} from 'datocms-plugin-sdk'
import 'datocms-react-ui/styles.css'
import ConfigScreen from './entrypoints/ConfigScreen'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Button, Canvas } from 'datocms-react-ui'
import {
  isFieldValueEmpty,
  parsePluginField,
  setFieldOfLanguageInState,
  updatePluginField,
  usePrevious,
} from './helpers'
import { TranslationsAdmin } from './translationsWindow'
import { render } from './utils/render'
import { LANGS } from './utils/langs'
import { diff } from 'deep-object-diff'
import { IPluginFieldType } from './types'

// Change This field needs to be translated to "translation required"
// Check if we can use the message for "field required"
// Custom visualization of the 2 fields
// Check if a visualization can be applied in language tab

// Make "missing translations" field translatable ( but still hidden )

// Mandatory field only for first case -- when you copy from the main language

// Page for admins -- copy for all labels content from german to NEW LANGUAGE
// Show all missing entries from all models

// Plugin not used -> Plugin to be added / only for admins / option to click and add fields automatically / not visible for translators

function renderError(ctx: any, error: React.ReactNode) {
  render(
    <Canvas ctx={ctx}>
      <div
        style={{
          fontSize: 'var(--font-size-m)',
          color: 'var(--alert-color)',
        }}
      >
        {error}
      </div>
    </Canvas>
  )
}

connect({
  mainNavigationTabs(ctx: IntentCtx) {
    return [
      {
        label: 'Translations',
        icon: 'analytics',
        pointsTo: {
          pageId: 'translations',
        },
      },
    ]
  },
  renderPage(pageId, ctx: RenderPageCtx) {
    switch (pageId) {
      case 'translations':
        return render(<TranslationsAdmin ctx={ctx} />)
    }
  },
  manualFieldExtensions(ctx: IntentCtx) {
    return [
      {
        id: 'missingTranslationsField',
        name: 'Missing Translations',
        type: 'editor',
        fieldTypes: ['rich_text', 'string', 'text'],
      },
      {
        id: 'missingTranslationsStore',
        name: 'Missing Translations',
        type: 'editor',
        fieldTypes: ['json'],
        initialHeight: 120,
        asSidebarPanel: {
          startOpen: true,
        },
      },
    ]
  },
  overrideFieldExtensions(field: Field, ctx: FieldIntentCtx) {
    if (field.attributes.localized) {
      return {
        addons: [{ id: 'missingTranslationIndicator' }],
      }
    }
  },
  renderFieldExtension(fieldExtensionId: string, ctx: RenderFieldExtensionCtx) {
    // Get the ID of the current model
    const currentModelId = ctx.field.relationships.item_type.data.id

    // Get the fields that belong to this model specifically
    const modelFields = Object.values(ctx.fields).filter(
      (field) => field?.relationships.item_type.data.id === currentModelId
    )

    // Find the field with the store of the plugin
    const storeField = modelFields.find(
      (item) =>
        item?.attributes.appearance.field_extension ===
        'missingTranslationsStore'
    )

    // Find the plugin text field
    const missingTranslationsField = modelFields.find(
      (item) =>
        item?.attributes.appearance.field_extension ===
        'missingTranslationsField'
    )

    // Parse the field ( if present )
    const existingState: any =
      storeField &&
      parsePluginField(
        ctx.formValues[storeField.attributes.api_key] as IPluginFieldType
      )

    const allLanguages = ctx.site.attributes.locales
    const addedLocales: string[] =
      (ctx.formValues.internalLocales as string[]) || ([] as string[])
    const mainLanguage: string =
      allLanguages.find((lang) => addedLocales.includes(lang)) || 'en'

    const notAddedLocales = allLanguages.filter(
      (lang) => !addedLocales.includes(lang)
    )

    // Handle when a radio button is pressed
    const onRadioChange = (value: boolean) => {
      const newFieldValue = {
        ...existingState,
      }
      setFieldOfLanguageInState(
        newFieldValue,
        ctx.locale,
        ctx.field.attributes.api_key,
        value
      )
      updatePluginField(
        ctx,
        newFieldValue,
        existingState,
        notAddedLocales,
        storeField?.attributes.api_key
      )
    }

    const translationRequired =
      existingState &&
      existingState[ctx.locale] &&
      existingState[ctx.locale][ctx.field.attributes.api_key]

    switch (fieldExtensionId) {
      case 'missingTranslationIndicator':
        return render(
          <Canvas ctx={ctx}>
            <div
              id={'field-translation-status-' + ctx.field.attributes.api_key}
              style={{
                color: '#ff5e49',
                fontSize: '.75rem',
                lineHeight: '1.2',
                float: ctx.plugin.attributes.parameters.forceCheckbox
                  ? 'left'
                  : 'none',
              }}
            >
              {translationRequired ? 'Translation required' : ''}
            </div>
            {storeField &&
              ctx.plugin.attributes.parameters.forceCheckbox &&
              mainLanguage !== ctx.locale && (
                <div
                  id={
                    'field-translation-controls-' + ctx.field.attributes.api_key
                  }
                  style={{
                    fontSize: '.5rem',
                    lineHeight: '1.2',
                    float: 'right',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '22px',
                  }}
                >
                  <label
                    htmlFor={
                      'custom-missing-translations-state-checkbox' +
                      ctx.field.attributes.api_key +
                      ' - ' +
                      ctx.locale
                    }
                  >
                    Translated:{' '}
                  </label>
                  <input
                    type="checkbox"
                    id={
                      'custom-missing-translations-state-checkbox' +
                      ctx.field.attributes.api_key +
                      ' - ' +
                      ctx.locale
                    }
                    name="custom-missing-translations-state"
                    onChange={() => onRadioChange(!translationRequired)}
                    checked={!translationRequired}
                  ></input>
                </div>
              )}
          </Canvas>
        )
      case 'hiddenField':
        return render(<Canvas ctx={ctx}>{''}</Canvas>)
      case 'missingTranslationsStore':
        // Check if the current field is translated
        if (!ctx.fields[ctx.field.id]?.attributes.localized) {
          renderError(
            ctx,
            <>
              <div>This JSON field must have localization enabled</div>
            </>
          )
          return
        }
        if (!missingTranslationsField) {
          renderError(
            ctx,
            <>
              <div>
                A text field with assigned Field Editor &quot;Missing
                Translations &quot; was not found. <br /> <br />
              </div>
              <div>Please, go to the model configuration and update this.</div>
            </>
          )
          return
        }
        if (
          !ctx.fields[missingTranslationsField.id || 0]?.attributes.localized
        ) {
          renderError(
            ctx,
            <>
              <div>
                The text field with key{' '}
                {missingTranslationsField.attributes.api_key} must have
                localization enabled
              </div>
            </>
          )
          return
        }
        ReactDOM.render(
          <React.StrictMode>
            <TranslatorPluginView
              ctx={ctx}
              missingTranslationsApiKey={
                missingTranslationsField.attributes.api_key
              }
            />
          </React.StrictMode>,
          document.getElementById('root')
        )
    }
  },
  renderConfigScreen(ctx: RenderConfigScreenCtx) {
    ReactDOM.render(
      <React.StrictMode>
        <ConfigScreen ctx={ctx} />
      </React.StrictMode>,
      document.getElementById('root')
    )
  },
})

type PropTypes = {
  ctx: RenderFieldExtensionCtx
  missingTranslationsApiKey: string
}

function TranslatorPluginView({ ctx, missingTranslationsApiKey }: PropTypes) {
  // Languages added in the settings of this Dato repository
  const allLanguages = ctx.site.attributes.locales

  // Keys of fields that will not be checked for missing translations
  const excludedFields = [
    missingTranslationsApiKey,
    ctx.field.attributes.api_key,
  ]

  const languageLabels = LANGS

  const formValues: any = ctx.formValues
  const addedLocales: string[] = formValues.internalLocales || ([] as string[])
  const notAddedLocales = allLanguages.filter(
    (lang) => !addedLocales.includes(lang)
  )
  const mainLanguage: string =
    allLanguages.find((lang) => addedLocales.includes(lang)) || 'en'

  // Missing translations state
  const [missingTranslations, setMissingTranslations] = useState<
    { lang: string; value: boolean }[]
  >(
    Object.keys(formValues[missingTranslationsApiKey]).map((lang) => {
      return {
        lang: lang,
        value: formValues[missingTranslationsApiKey][lang] === 'YES',
      }
    })
  )

  // Used to indicate that currently there is a translation after copy, and indicates which language
  const [currentlyInputtingAfterCopy, setCurrentlyInputtingAfterCopy] =
    useState<string | undefined>(undefined)

  const prevFormValues: any = usePrevious(formValues)

  /**
   * Makes the missing translations field hidden
   */
  useEffect(() => {
    for (const lang of allLanguages) {
      ctx.toggleField(missingTranslationsApiKey + '.' + lang, false)
    }
  }, [missingTranslationsApiKey])

  useEffect(() => {
    if (ctx.item?.id) {
      const currentItemId = ctx.item.id + ctx.item.attributes.updated_at
      if (
        localStorage.getItem('itemId') &&
        localStorage.getItem('itemId') !== currentItemId
      ) {
        localStorage.setItem('addedLocalesState', JSON.stringify(addedLocales))
      }
      localStorage.setItem('itemId', currentItemId)
    }
  }, [ctx.item?.id, ctx.item?.attributes.updated_at])

  /**
   * Main listener for changes in the form
   * Decides which fields need to be translated
   */
  useEffect(() => {
    const currentPluginContent = parsePluginField(
      ctx.formValues[ctx.field.attributes.api_key] as any
    )
    if (!currentPluginContent) {
      return
    }

    // Get only the fields that are translatable and not part of the plugin
    const translatableFieldKeys = Object.keys(formValues).filter(
      (key) =>
        !excludedFields.includes(key) &&
        formValues[key] instanceof Object &&
        Object.keys(formValues[key]).includes(mainLanguage)
    )

    if (prevFormValues) {
      // Calculate difference in formValues and the keys of the differences that are in translatable fields
      const differences: any = diff(prevFormValues, formValues)
      const differentFieldKeys = Object.keys(differences)
        .filter((fieldKey) => translatableFieldKeys.includes(fieldKey))
        .filter((diffKey) => {
          const diffValue = differences[diffKey]
          if (
            diffValue instanceof Object &&
            Object.values(diffValue).every(
              (item) =>
                item instanceof Object &&
                Object.values(item).every(
                  (item2) =>
                    Object.keys(diff(Object.keys(item2), ['itemId'])).length ===
                    0
                )
            )
          ) {
            return false
          }
          return true
        })

      for (const fieldKey of differentFieldKeys) {
        for (const lang of Object.keys(differences[fieldKey])) {
          if (lang === mainLanguage) {
            // If the main language is changed, set all other languages to "field needs translation"
            allLanguages
              .filter((lang) => lang !== mainLanguage)
              .forEach((otherLang) => {
                setFieldOfLanguageInState(
                  currentPluginContent,
                  otherLang,
                  fieldKey,
                  true
                )
              })
          } else {
            // If the changed field is not in the main language, update it to translated
            setFieldOfLanguageInState(
              currentPluginContent,
              lang,
              fieldKey,
              false
            )
          }
        }
      }
    }

    const addedLocalesState = localStorage.getItem('addedLocalesState')
      ? JSON.parse(localStorage.getItem('addedLocalesState') || '[]')
      : undefined

    if (addedLocalesState) {
      // Detect if there was a new language added in the tab of the record
      const newlyAddedLocales = formValues.internalLocales.filter(
        (locale: any) => !addedLocalesState.includes(locale)
      )

      for (const newlyAddedLocale of newlyAddedLocales) {
        for (const field of translatableFieldKeys) {
          // For each field in a newly added locale, set it to "needs translation"
          setFieldOfLanguageInState(
            currentPluginContent,
            newlyAddedLocale,
            field,
            true
          )
        }
      }
    }
    localStorage.setItem('addedLocalesState', JSON.stringify([...addedLocales]))

    // This is the strongest rule, thus it comes last
    // For all fields in the main language
    //  Set to "does not need a translation"
    // For all fields not in the main language
    // If field is empty --
    //  Set to "needs translation" if corresponding field in main language is not empty
    //  Set to "not translation needed" otherwise
    for (const field of translatableFieldKeys) {
      for (const lang of Object.keys(formValues[field])) {
        if (lang === mainLanguage) {
          setFieldOfLanguageInState(currentPluginContent, lang, field, false)
        } else if (isFieldValueEmpty(formValues[field][lang])) {
          setFieldOfLanguageInState(
            currentPluginContent,
            lang,
            field,
            !isFieldValueEmpty(formValues[field][mainLanguage])
          )
        }
      }
    }

    // Write the new changes to the plugin field
    updatePluginField(
      ctx,
      currentPluginContent,
      formValues[ctx.field.attributes.api_key],
      notAddedLocales
    )

    // Calculate which languages need to be translated, and update the state
    const newMissingTranslations = Object.keys(currentPluginContent)
      .filter((langKey) => {
        return Object.values(currentPluginContent[langKey]).includes(true)
      })
      .concat(notAddedLocales)

    setMissingTranslations(
      allLanguages.map((language) => {
        return {
          lang: language,
          value: newMissingTranslations.includes(language),
        }
      })
    )
  }, [formValues])

  /**
   * Listen for changes in the state and update the missing languages text field accordingly
   */
  useEffect(() => {
    allLanguages.forEach((iso) => {
      const missingTranslationField = missingTranslations.find(
        (translation) => translation.lang === iso
      )
      const missingTranslationsString = missingTranslationField?.value
        ? 'YES'
        : 'NO'

      // If there was just a new language created, do not set any text to trigger the required validator
      if (currentlyInputtingAfterCopy === iso) {
        if (!missingTranslationField?.value) {
          setCurrentlyInputtingAfterCopy(undefined)
          return
        }
        const newValue = null
        if (formValues[missingTranslationsApiKey][iso] !== newValue) {
          ctx.setFieldValue(missingTranslationsApiKey + '.' + iso, newValue)
        }
      } else if (
        !notAddedLocales.includes(iso) &&
        formValues[missingTranslationsApiKey][iso] !== missingTranslationsString
      ) {
        ctx.setFieldValue(
          missingTranslationsApiKey + '.' + iso,
          missingTranslationsString
        )
      }
    })
  }, [
    allLanguages,
    missingTranslations,
    formValues,
    currentlyInputtingAfterCopy,
  ])

  /**
   * Handle copying the text from the main locale to the current
   * @param locale - ISO code of the language to copy fields
   */
  const translateTo = async (locale: string) => {
    setCurrentlyInputtingAfterCopy(locale)
    for (const key of Object.keys(formValues)) {
      const formValue = formValues[key]
      // Handle copying for translatable fields
      if (
        formValue instanceof Object &&
        Object.keys(formValue).includes(mainLanguage)
      ) {
        await ctx.setFieldValue(key + '.' + locale, formValue[mainLanguage])
      }
    }
    await ctx.setFieldValue('internalLocales', [...addedLocales, locale])
  }

  if (ctx.itemStatus === 'new') {
    // we're in a record that still has not been persisted
    return <div>Please save the record first!</div>
  }

  return (
    <Canvas ctx={ctx}>
      <span style={{ fontSize: '0.75rem', paddingBottom: '12px' }}>
        Main language: {languageLabels[mainLanguage]} <br />
      </span>
      {!!(notAddedLocales.length && addedLocales.includes(mainLanguage)) &&
        false && (
          <>
            Copy from {languageLabels[mainLanguage]} <br />
            {notAddedLocales.map((language) => (
              <>
                <Button onClick={() => translateTo(language)}>
                  To {languageLabels[language]}
                </Button>
                <div style={{ marginBottom: '10px' }}></div>
              </>
            ))}{' '}
            <br />
          </>
        )}
      Present translations <br />
      {allLanguages.map((language) => (
        <>
          <input
            type="checkbox"
            checked={
              !missingTranslations.find((item) => item.lang === language)?.value
            }
            readOnly={true}
          ></input>
          <label>{languageLabels[language]}</label> <br />
        </>
      ))}
    </Canvas>
  )
}
